import React, { useCallback, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

interface PullToRefreshProps {
  children: React.ReactNode;
}

const PULL_THRESHOLD = 80;

const PullToRefresh: React.FC<PullToRefreshProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const isTouching = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    if (scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
      isTouching.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isTouching.current || isRefreshing) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) {
      const distance = Math.min(diff * 0.4, 120);
      setPullDistance(distance);
      setIsPulling(true);
    }
  }, [isRefreshing]);

  const handleTouchEnd = useCallback(async () => {
    isTouching.current = false;
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(PULL_THRESHOLD * 0.5);
      await queryClient.invalidateQueries();
      setIsRefreshing(false);
    }
    setPullDistance(0);
    setIsPulling(false);
  }, [pullDistance, isRefreshing, queryClient]);

  return (
    <div onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      {/* Pull-to-refresh indicator */}
      <div
        className="flex items-center justify-center overflow-hidden transition-all duration-200 ease-out"
        style={{
          height: isPulling || isRefreshing ? `${pullDistance}px` : '0px',
          opacity: pullDistance > 10 ? 1 : 0,
        }}
      >
        <RefreshCw
          className={`h-5 w-5 text-primary transition-transform duration-200 ${isRefreshing ? 'animate-spin' : ''}`}
          style={{
            transform: isRefreshing
              ? undefined
              : `rotate(${Math.min((pullDistance / PULL_THRESHOLD) * 360, 360)}deg)`,
          }}
        />
        <span className="ml-2 text-xs text-muted-foreground">
          {isRefreshing ? 'Refreshing...' : pullDistance >= PULL_THRESHOLD ? 'Release to refresh' : 'Pull to refresh'}
        </span>
      </div>
      {children}
    </div>
  );
};

export default PullToRefresh;
